import { useState, useEffect, useRef } from 'react'
import { useKeyboardOffset } from '../../hooks/useKeyboardOffset'
import './GameRoomSidebar.css'

export interface GameState {
  phase?: string
  turn?: string
  timer?: number
  score?: Record<string, number | string>
}

export interface Message {
  sender: 'you' | 'opponent' | 'ai' | 'system'
  content: string
  timestamp: number
}

export interface HistoryItem {
  type: 'question' | 'answer' | 'move' | 'event'
  content: string
  timestamp: number
}

export interface GameRoomSidebarProps {
  mode: 'ai' | 'network' | 'local'
  gameState: GameState
  messages: Message[]
  historyItems?: HistoryItem[]
  onSendMessage: (message: string) => void
  onToggleCollapse?: () => void
  collapsed?: boolean
  position?: 'docked' | 'floating' | 'overlay' | 'bottom-sheet'
  quickReactions?: boolean
}

export function GameRoomSidebar({
  mode,
  gameState,
  messages,
  historyItems = [],
  onSendMessage,
  onToggleCollapse,
  collapsed = false,
  position = 'docked',
  quickReactions = true
}: GameRoomSidebarProps) {
  const [inputValue, setInputValue] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [startY, setStartY] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const logEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const keyboardOffset = useKeyboardOffset()
  const sidebarRef = useRef<HTMLElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [historyItems])

  useEffect(() => {
    if (position !== 'bottom-sheet' || !sidebarRef.current || !onToggleCollapse) return
    const sidebar = sidebarRef.current

    const handleTouchStart = (e: TouchEvent) => {
      setIsDragging(true)
      setStartY(e.touches[0].clientY)
      sidebar.classList.add('dragging')
    }
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return
      const deltaY = e.touches[0].clientY - startY
      if (Math.abs(deltaY) > 10) e.preventDefault()
    }
    const handleTouchEnd = (e: TouchEvent) => {
      if (!isDragging) return
      const deltaY = e.changedTouches[0].clientY - startY
      sidebar.classList.remove('dragging')
      setIsDragging(false)
      if (Math.abs(deltaY) > 50) {
        if (deltaY > 0 && !collapsed) onToggleCollapse()
        else if (deltaY < 0 && collapsed) onToggleCollapse()
      }
    }
    const handleClick = (e: MouseEvent) => {
      if (!collapsed) return
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button')) return
      onToggleCollapse()
    }

    sidebar.addEventListener('touchstart', handleTouchStart, { passive: false })
    sidebar.addEventListener('touchmove', handleTouchMove, { passive: false })
    sidebar.addEventListener('touchend', handleTouchEnd)
    sidebar.addEventListener('click', handleClick)
    return () => {
      sidebar.removeEventListener('touchstart', handleTouchStart)
      sidebar.removeEventListener('touchmove', handleTouchMove)
      sidebar.removeEventListener('touchend', handleTouchEnd)
      sidebar.removeEventListener('click', handleClick)
    }
  }, [position, isDragging, startY, collapsed, onToggleCollapse])

  const handleSend = () => {
    if (!inputValue.trim()) return
    onSendMessage(inputValue.trim())
    setInputValue('')
    inputRef.current?.focus()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const getSenderLabel = (sender: Message['sender']) => {
    switch (sender) {
      case 'you': return 'You'
      case 'opponent': return 'Opponent'
      case 'ai': return '🤖 AI'
      case 'system': return 'System'
    }
  }

  const getLogIcon = (type: HistoryItem['type']) => {
    switch (type) {
      case 'move': return '♟️'
      case 'event': return '⚡'
      case 'question': return '❓'
      case 'answer': return '✅'
    }
  }

  const getModeTitle = () => {
    switch (mode) {
      case 'local': return '📋 Game Log'
      case 'ai': return '🤖 AI Chat'
      case 'network': return '💬 Chat'
    }
  }

  if (collapsed && (position === 'floating' || position === 'overlay')) {
    return (
      <button className="sidebar-toggle-button" onClick={onToggleCollapse} aria-label="Open sidebar">
        {mode === 'local' ? '📋' : '💬'}
      </button>
    )
  }

  const isBottomSheet = position === 'bottom-sheet'
  const sidebarClasses = `game-room-sidebar ${position} ${isBottomSheet ? 'bottom-sheet' : ''} ${collapsed ? 'collapsed' : 'expanded'}`
  const isFixed = position === 'overlay' || position === 'bottom-sheet' || position === 'floating'
  const keyboardStyle = isFixed && keyboardOffset > 0 ? { transform: `translateY(-${keyboardOffset}px)` } : undefined

  const showInput = mode !== 'local'

  /* Collapsed bottom-sheet: show a slim status strip instead of blank space */
  if (isBottomSheet && collapsed && onToggleCollapse) {
    const scoreText = gameState.score
      ? Object.values(gameState.score).slice(0, 2).join(' · ')
      : null
    return (
      <div className="bottom-sheet-strip" onClick={onToggleCollapse} role="button" aria-label="Open game panel">
        <div className="strip-handle" />
        <div className="strip-row">
          <span className="strip-mode-icon">{mode === 'local' ? '📋' : '💬'}</span>
          {gameState.turn && <span className="strip-turn">{gameState.turn}</span>}
          {scoreText && <span className="strip-score">{scoreText}</span>}
          <span className="strip-expand-hint">↑</span>
        </div>
      </div>
    )
  }

  return (
    <aside ref={sidebarRef} className={sidebarClasses} data-mode={mode} style={keyboardStyle}>

      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-title">
          <h2 className="title-text">{getModeTitle()}</h2>
        </div>
        <div className="sidebar-actions">
          {onToggleCollapse && position !== 'docked' && (
            <button className="close-button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleCollapse() }} aria-label="Close" type="button">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Game State Panel */}
      {(gameState.phase || gameState.turn || gameState.timer !== undefined || gameState.score) && (
        <div className="game-state-panel">
          {gameState.turn && (
            <div className="state-item turn-item">
              <span className="state-label">Turn</span>
              <span className="state-value">{gameState.turn}</span>
            </div>
          )}
          {gameState.phase && (
            <div className="state-item">
              <span className="state-label">Phase</span>
              <span className="state-value">{gameState.phase}</span>
            </div>
          )}
          {gameState.timer !== undefined && (
            <div className="state-item timer-item">
              <span className="state-label">⏱️ Time</span>
              <span className="state-value timer-value">
                {Math.floor(gameState.timer / 60)}:{String(gameState.timer % 60).padStart(2, '0')}
              </span>
            </div>
          )}
          {gameState.score && (
            <div className="state-item score-item" style={{ gridColumn: '1 / -1' }}>
              <span className="state-label">🏆 Score</span>
              <span className="state-value">
                <span className="score-entries">
                  {Object.entries(gameState.score).map(([player, score]) => (
                    <span key={player} className="score-entry">{player}: {score}</span>
                  ))}
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="messages-container">

        {/* Local mode: game log feed as primary content */}
        {mode === 'local' && (
          <div className="log-feed">
            {historyItems.length === 0 ? (
              <div className="empty-state"><p>Game log will appear here</p></div>
            ) : (
              historyItems.map((item, i) => (
                <div key={i} className={`log-entry ${item.type}`}>
                  <span className="log-icon">{getLogIcon(item.type)}</span>
                  <span className="log-text">{item.content}</span>
                  <span className="log-time">{formatTime(item.timestamp)}</span>
                </div>
              ))
            )}
            <div ref={logEndRef} />
          </div>
        )}

        {/* AI mode: messages + history items */}
        {mode === 'ai' && (
          <div className="messages-list">
            {messages.length === 0 && historyItems.length === 0 ? (
              <div className="empty-state"><p>Ask me questions to narrow it down!</p></div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <div key={i} className={`message message-${msg.sender}`}>
                    <div className="message-header">
                      <span className="sender-name">{getSenderLabel(msg.sender)}</span>
                      <span className="message-time">{formatTime(msg.timestamp)}</span>
                    </div>
                    <div className="message-content">{msg.content}</div>
                  </div>
                ))}
                {historyItems.length > 0 && (
                  <div className="log-feed" style={{ marginTop: '8px' }}>
                    {historyItems.map((item, i) => (
                      <div key={i} className={`log-entry ${item.type}`}>
                        <span className="log-icon">{getLogIcon(item.type)}</span>
                        <span className="log-text">{item.content}</span>
                        <span className="log-time">{formatTime(item.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Network mode: chat messages */}
        {mode === 'network' && (
          <div className="messages-list">
            {messages.length === 0 ? (
              <div className="empty-state"><p>Start chatting with your opponent</p></div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`message message-${msg.sender}`}>
                  <div className="message-header">
                    <span className="sender-name">{getSenderLabel(msg.sender)}</span>
                    <span className="message-time">{formatTime(msg.timestamp)}</span>
                  </div>
                  <div className="message-content">{msg.content}</div>
                </div>
              ))
            )}
            {historyItems.length > 0 && (
              <div className="log-feed" style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '8px' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', padding: '0 4px' }}>GAME LOG</p>
                {historyItems.slice(-10).map((item, i) => (
                  <div key={i} className={`log-entry ${item.type}`}>
                    <span className="log-icon">{getLogIcon(item.type)}</span>
                    <span className="log-text">{item.content}</span>
                    <span className="log-time">{formatTime(item.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area (hidden for local mode) */}
      {showInput && (
        <div className="input-container">
          {mode === 'network' && quickReactions && (
            <div className="quick-reactions">
              {['👍', '❤️', '😂', '🎉'].map((reaction) => (
                <button key={reaction} className="reaction-button" onClick={() => onSendMessage(reaction)} aria-label={`Send ${reaction}`}>
                  {reaction}
                </button>
              ))}
            </div>
          )}
          <div className="input-row">
            <input
              ref={inputRef}
              type="text"
              className="message-input"
              placeholder={mode === 'ai' ? 'Type your question...' : 'Type a message...'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button className="send-button" onClick={handleSend} disabled={!inputValue.trim()} aria-label="Send">
              →
            </button>
          </div>
          {mode === 'ai' && (
            <div className="input-hint">
              <small>Example: "Does your person have glasses?"</small>
            </div>
          )}
        </div>
      )}
    </aside>
  )
}
